import { Trans, useTranslation } from 'react-i18next';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import { getEmbeddedSignupAttemptId } from 'src/lib/facebook/embeddedSignupTelemetry';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_CAPTION_CLASS_NAME,
  WIZARD_CONNECTION_VIDEO_URL,
  WIZARD_SCREEN_SUBTITLE_CLASS_NAME,
  WIZARD_SCREEN_TITLE_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import { resolveClosedStep } from './WhatsAppConnectionWizard.functions';
import WhatsAppConnectionWizardExternalLink, {
  WhatsAppConnectionWizardAdvisorRow,
} from './WhatsAppConnectionWizardExternalLink.component';
import type { tWizardConnectionVideoLocation } from './WhatsAppConnectionWizard.types';

export type tWhatsAppConnectionWizardClosedBeforeFinishingProps = {
  /** Meta's raw `current_step` of the close, or null when it named none. */
  step: string | null;
  /** Whether the previous close had the same step. */
  isRepeat: boolean;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
  /** Reports a click on the connection video link and the screen it was on. */
  onConnectionVideoClick: (location: tWizardConnectionVideoLocation) => void;
};

/**
 * The main pane when Meta's window closes before the connection finishes,
 * with no error reported. It names the Meta screen of the close and its hint;
 * a second close on that screen points to an advisor, whom the footer then
 * leads with. With no screen to name, it gives the attempt's code instead.
 * Every variant links the connection video, which shows the screens the
 * person just went through.
 *
 * @param {tWhatsAppConnectionWizardClosedBeforeFinishingProps} props - The
 * close and the advisor and video click reports.
 * @returns {JSX.Element} The closed-before-finishing screen.
 */
export default function WhatsAppConnectionWizardClosedBeforeFinishing(
  props: tWhatsAppConnectionWizardClosedBeforeFinishingProps,
) {
  const { onAdvisorCtaClick, onConnectionVideoClick } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const { metaStep, isRepeat } = resolveClosedStep(props);
  const attemptId = getEmbeddedSignupAttemptId();
  const connectionVideoLink = (
    <WhatsAppConnectionWizardExternalLink
      href={WIZARD_CONNECTION_VIDEO_URL}
      label={t('wizard.actions.connectionVideo')}
      variant="quiet"
      onClick={() =>
        onConnectionVideoClick(
          isRepeat ? 'meta_window_closed_repeat' : 'meta_window_closed',
        )
      }
    />
  );

  let subtitle = t('wizard.closedBeforeFinishing.subtitleNoStep');
  if (metaStep) {
    subtitle = t(
      isRepeat
        ? 'wizard.closedBeforeFinishing.subtitleRepeat'
        : 'wizard.closedBeforeFinishing.subtitleStep',
      { step: t(`wizard.metaSteps.${metaStep}.title`) },
    );
  }

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-6)]">
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <h2 className={WIZARD_SCREEN_TITLE_CLASS_NAME}>
          {t('wizard.closedBeforeFinishing.title')}
        </h2>
        <p className={WIZARD_SCREEN_SUBTITLE_CLASS_NAME}>{subtitle}</p>
      </div>
      {metaStep ? (
        <div className="flex flex-col items-start gap-[var(--ls-spacing-4)]">
          <p className={WIZARD_BODY_CLASS_NAME}>
            <Trans
              t={t}
              i18nKey={`wizard.metaSteps.${metaStep}.hint`}
              components={{ strong: <strong className="font-medium" /> }}
            />
          </p>
          {connectionVideoLink}
        </div>
      ) : (
        // With no cause to diagnose, the attempt's code is what lets an
        // advisor find the case.
        <div className="flex flex-col items-start gap-[var(--ls-spacing-2)]">
          {attemptId && (
            <p className={WIZARD_CAPTION_CLASS_NAME}>
              {t('wizard.closedBeforeFinishing.attemptCodeLine', {
                attemptId,
              })}
            </p>
          )}
          {connectionVideoLink}
        </div>
      )}
      {/* A repeated close puts the advisor in the footer's primary. */}
      {!isRepeat && (
        <WhatsAppConnectionWizardAdvisorRow
          onAdvisorCtaClick={onAdvisorCtaClick}
        />
      )}
    </div>
  );
}
